<template>
  <div
    class="d-flex flex-column fill-height"
  >
    <!-- First row: alert, fixed height -->
    <v-row
      no-gutters
      class="flex-grow-0"
    >
      <v-col
        cols="12"
      >
        <v-alert
          v-if="editMode"
          type="info"
          class="mb-0 py-3 rounded-0"
          v-html="alertText"
        />
      </v-col>
    </v-row>

    <!-- Second row: takes up remaining space -->
    <v-row
      no-gutters
      class="flex-grow-1 overflow-hidden table-map-row"
    >
      <!-- Left side: table -->
      <v-col
        md="4"
        sm="12"
        class="table-col"
      >
        <table-index
          v-if="tableVisibility"
          :config="tableConfig"
          :create-config="createConfig"
        />
        <tabs-layout
          v-else
          :config="editConfig"
        />
      </v-col>

      <!-- Right side: map -->
      <v-col
        md="8"
        sm="12"
        class="map-col"
      >
        <locations-map
          class="locations-map fill-height"
          :edit-data="editLocationData"
          :edit-mode="editMode"
          :selected-locations="selectedLocations"
          :location-type="locationType"
          :create-btn-text="'Novo'"
          :view-config="{}"
          :cancel-shape="cancelShape"
          :confirm-shape="confirmShape"
          :shape-type="limitExceedDialogType"
          @input="handleMapDataChange"
          @create="handleCreate"
          @save="handleSave"
          @show-polygon-max-points-message="alertMessage => updateAlertText(alertMessage)"
          @update-type="dataType = $event"
          @show-exceed-limit-dialog="setShowLimitExceedDialog"
          @reset-cancel-shape="cancelShape = false"
          @reset-confirm-shape="confirmShape = false"
        />
      </v-col>
    </v-row>
    <v-dialog
      v-model="showLimitExceedDialog"
      :max-width="$vuetify.breakpoint.mdAndUp ? '40%' : '80%'"
      persistent
      :transition="false"
    >
      <v-card>
        <v-card-title
          style="background-color: #eeeeee; font-size: 1.2rem;"
        >
          <v-icon
          >
            mdi-alert
          </v-icon>
          <span
            v-html="limitExceedDialogTitle"
            class="ml-3"
          />
        </v-card-title>
        <v-card-text>
          <span v-html="limitExceedDialogMessage" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="error"
            @click="setCancelShape"
          >
            {{ $t('locations/map.limit_exceed_dialog_cancel_button')}}
          </v-btn>
          <v-btn
            class="primary"
            @click="setConfirmShape"
          >
            {{ $t('locations/map.limit_exceed_dialog_confirm_button')}}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <information-dialog
      v-if="informationDialogConfig.showDialog"
      :open-dialog="informationDialogConfig.showDialog"
      :dialog-message="informationDialogConfig.message"
      :title="informationDialogConfig.title"
      :positive-button-text="informationDialogConfig.positiveButtonText"
      :negative-button-text="informationDialogConfig.negativeButtonText"
      :confirmCallbackFunction="informationDialogConfig.confirmCallbackFunction"
      @close="onCloseInformationDialog"
    />

  </div>
</template>

<script>
import LocationsMap from '../components/LocationsMap'
import TableIndex from '@/global/components/table/TableIndex.vue'
import ColoredCell from '../components/ColoredCell.vue'
import TabActionButtonsWithTitle from '@/global/components/view-layouts/tabs-layout/components/TabActionButtonsWithTitle.vue'
import FormFields from '../components/FormFields.vue'
import { isArray } from 'lodash'
import InformationDialog from '@/global/components/view-layouts/tabs-layout/components/dialogs/InformationDialog.vue'
import { api } from '@/global/services/api'
import TabsLayout from '@/global/components/view-layouts/tabs-layout/views/TabsLayout.vue'
import store from '@/global/store'
import BooleanCell from '../components/BooleanCell.vue'

export default {
  name: 'LocationsIndexView',

  components: {
    TabsLayout,
    InformationDialog,
    TableIndex,
    LocationsMap
  },
  props: {
    module: {
      type: String,
      default: 'locations'
    },
    model: {
      type: String,
      default: 'locations'
    }
  },
  data () {
    return {
      selectedLocations: [],
      dataType: null,
      locationData: null,
      editLocationData: null,
      showEditDialog: false,
      resetSelection: false,
      resetForm: false,
      editMode: false,
      baseAlertText: this.$t('locations/location.help_text'),
      alertText: this.$t('locations/location.help_text'),
      limitExceedDialogLimitParameter: '',
      limitExceedDialogType: '',
      showLimitExceedDialog: false,
      cancelShape: false,
      confirmShape: false,
      limitExceedDialogTitle: '',
      limitExceedDialogMessage: '',
      informationDialogConfig: {
        showDialog: false,
        message: '',
        title: '',
        positiveButtonText: this.$t('base.save'),
        negativeButtonText: this.$t('base.cancel'),
        confirmCallbackFunction: null
      },
      tableVisibility: true,
      editRoute: 'locations',
      formDataEdit: false
    }
  },
  computed: {
    locationType () {
      const routeType = this.$route.path.split('/locations/')[1]
      const locationTypeMap = {
        city: 1,
        region: 2,
        country: 3,
        user_defined: 4,
        city_district: 5
      }

      return locationTypeMap[routeType]
    },

    tableConfig () {
      return {
        tableConfig: {
          visible: this.tableVisibility,
          apiConfig: {
            get: {
              module: 'locations',
              route: 'locations',
              method: 'get'
            }
          },
          queryParams: (event) => {
            const { params } = event || {}
            params.filters = {
              ...params.filters,
              type_id: this.locationType
            }

            return params
          },
          rowsSelection: {
            showCheckboxes: true,
            rememberSelectionByDataProperty: 'id',
            selectionsChange: (event) => this.handleLocationSelection(event)
          },
          rowClick: (event) => this.showLocationDetails(event),
          title_and_back_config: {
            title: this.$t('locations.locations')
          },
          search_and_buttons_config: {
            newClick: (event) => this.onCreateNewLocation(event),
            showNewBtn: this.locationType === 4
          },
          tableConfig: {
            displayActions: this.locationType === 4,
            styles: {
              useFixedHeaderWidth: true,
              headersHeight: '76px',
              dataRowHeight: '64px'
            },
            cellsConfig: {
              data_type: {
                style: {
                  whiteSpace: 'noWrap'
                },
                custom_display_value: (data) => {
                  const { display_name: displayName } = data?.data_type || {}
                  return displayName || ''
                }
              },
              default: {
                style: {
                  textAlign: 'center'
                }
              },
              parameter: {
                style: {
                  textAlign: 'center'
                }
              }
            },
            headers: [
              {
                label: this.$t('locations/location.data_type'),
                key: 'data_type',
                style: {
                  width: '100px'
                }
              },
              {
                label: this.$t('locations/location.name'),
                key: 'name',
                style: {
                  width: '200px'
                },
                always_visible: true
              },
              {
                label: this.$t('locations/location.main_location'),
                key: 'default',
                style: {
                  width: '220px'
                },
                component: BooleanCell
              },
              {
                label: this.$t('locations/location.color'),
                key: 'color',
                style: {
                  width: '120px'
                },
                component: ColoredCell,
                always_visible: true
              },
              {
                label: this.$t('locations/location.parameter'),
                key: 'parameter',
                style: {
                  width: '100px'
                }
              }
            ],
            paginationConfig: {
              perPageOptions: [20, 50, 100],
              perPage: 20,
              currentPage: 1,
              totalVisible: 4
            },
            actionsConfig: [
              {
                label: this.$t('base.edit'),
                icon: 'mdi-pencil-box',
                click: (event) => this.showLocationDetails(event)
              },
              {
                label: (event) => {
                  const { deactivated_at: deactivatedAt } = event?.data || {}
                  return deactivatedAt ? this.$t('base.activate') : this.$t('base.deactivate')
                },
                icon: (event) => {
                  const { deactivated_at: deactivatedAt } = event?.data || {}
                  return deactivatedAt ? 'mdi-check-bold' : 'mdi-close-thick'
                },
                click: (event) => this.onLocationActivation(event)
              },
              {
                label: this.$t('base.delete'),
                icon: 'mdi-delete',
                click: (event) => this.onLocationDelete(event)
              }
            ],
            filtersConfig: {
              fields: [
                {
                  key: 'name',
                  label: this.$t('locations/location.name')
                }
              ]
            }
          }
        }
      }
    },

    createConfig () {
      return {
        tabs: [
          {
            title: this.$t('locations.location'),
            apiConfig: {
              post: {
                module: 'locations',
                route: 'locations',
                method: 'post'
              }
            },
            hiddenFormKeys: [
              'name',
              'default',
              'color',
              'data',
              'data_type_id'
            ],
            modifyDataBeforeSend: (event) => this.modifyDataBeforeSend(event),
            afterSave: (event) => this.afterSave(event),
            abortSave: (event) => this.checkCanSave(event),
            tabContent: [
              {
                component: TabActionButtonsWithTitle,
                title: this.$t('locations/location.new'),
                showEditButtons: true,
                onCancelTabSave: (event) => this.onCancelLocationManage(event)
              },
              {
                component: FormFields
              }
            ]
          }
        ]
      }
    },

    editConfig () {
      return {
        tabs: [
          {
            title: this.$t('locations.location'),
            key: 'location',
            apiConfig: {
              post: {
                module: 'locations',
                route: this.editRoute,
                method: 'post'
              },
              get: {
                module: 'locations',
                route: this.editRoute,
                method: 'get'
              }
            },
            hiddenFormKeys: [
              'name',
              'default',
              'color',
              'data',
              'data_type_id'
            ],
            editUpdate: (event) => this.onEditUpdate(event),
            modifyDataBeforeSend: (event) => this.modifyDataBeforeSend(event),
            afterSave: (event, data) => this.afterSave(event, data),
            abortSave: (event) => this.checkCanSave(event),
            tabContent: [
              {
                component: TabActionButtonsWithTitle,
                title: this.$t('locations/location.edit'),
                showEditButtons: true,
                showBackButton: {
                  onGoBack: () => this.backFromEdit()
                },
                onCancelTabSave: (event) => this.onCancelLocationManage(event)
              },
              {
                component: FormFields
              }
            ]
          }
        ]
      }
    }
  },
  methods: {
    updateAlertText (alertMessage) {
      this.alertText = alertMessage
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
    },

    handleLocationSelection (event) {
      const { data } = event || {}
      if (data && isArray(data)) {
        this.selectedLocations = data
      }
    },

    handleMapDataChange (data) {
      this.locationData = data
    },

    handleCreate () {
      this.locationData = null
      this.alertText = this.baseAlertText
      this.editMode = true
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
      this.resetSelection = true
    },

    setShowLimitExceedDialog (parameters) {
      if (parameters && parameters.type && parameters.limit) {
        this.showLimitExceedDialog = true
        this.limitExceedDialogLimitParameter = parameters.limit
        this.limitExceedDialogType = parameters.type

        this.limitExceedDialogTitle = this.limitExceedDialogType === 'polygon' ? this.$t('locations/map.limit_polygon_warning_message').replace(':limit', this.limitExceedDialogLimitParameter) : this.$t('locations/map.limit_polyline_warning_message').replace(':limit', this.limitExceedDialogLimitParameter)

        this.limitExceedDialogMessage = this.$t('locations/map.limit_exceed_dialog_cancellation_message').replace(':limit', this.limitExceedDialogLimitParameter).replace(':type', this.limitExceedDialogType) + '<br>' + this.$t('locations/map.limit_exceed_dialog_confirmation_message').replace(':limit', this.limitExceedDialogLimitParameter).replace(':type', this.limitExceedDialogType)
      }
    },

    setCancelShape () {
      this.showLimitExceedDialog = false
      this.cancelShape = true
      this.locationData = null
      // Do not clear shape when is edit mode
      if (!this.formDataEdit) {
        this.editLocationData = null
      }
    },

    setConfirmShape () {
      this.showLimitExceedDialog = false
      this.confirmShape = true
    },

    handleSave () {
      if (!this.locationData) {
        return
      }
      this.showLimitExceedDialog = false
      this.showEditDialog = true
    },

    onEditUpdate (event) {
      const { edit } = event || {}
      this.editMode = !!edit
    },

    onCreateNewLocation (event) {
      if (event && event.showTableNewPage && typeof event.showTableNewPage === 'function') {
        event.showTableNewPage()
      }
      this.editMode = true
      this.alertText = this.baseAlertText
      this.dataType = null
      this.locationData = null
    },

    onCancelLocationManage (event) {
      const { cancelTabSave } = event || {}

      if (typeof cancelTabSave === 'function') {
        cancelTabSave()
      }

      if (!this.formDataEdit) {
        this.backFromEdit()
      }
      else {
        this.editMode = false
        this.dataType = null
        this.locationData = null
      }
    },

    modifyDataBeforeSend (event) {
      const { originalData } = event || {}

      if (originalData) {
        originalData.data = this.locationData
        originalData.data_type_id = this.dataType
      }

      return originalData
    },

    afterSave (event, data = null) {
      const { cancelTabSave } = event || {}

      if (typeof cancelTabSave === 'function') {
        cancelTabSave()
      }

      this.editMode = false
      this.dataType = null
      this.locationData = null
      this.selectedLocations = []
      if (this.formDataEdit && data) {
        this.editLocationData = data
      }
      else {
        this.editLocationData = null
        this.formDataEdit = false
      }
    },

    onLocationDelete (event) {
      this.$set(this.informationDialogConfig, 'message', this.$t('locations/location.confirm_removal'))
      this.$set(this.informationDialogConfig, 'title', this.$t('locations/location.delete'))
      this.$set(this.informationDialogConfig, 'confirmCallbackFunction', () => this.onLocationDeleteConfirmation(event))
      this.$set(this.informationDialogConfig, 'showDialog', true)
    },

    onLocationActivation (event) {
      const { deactivated_at: deactivatedAt } = event?.data || {}

      if (deactivatedAt) {
        this.$set(this.informationDialogConfig, 'title', this.$t('locations/location.activate'))
        this.$set(this.informationDialogConfig, 'message', this.$t('locations/location.confirm_activation'))
      }
      else {
        this.$set(this.informationDialogConfig, 'title', this.$t('locations/location.deactivate'))
        this.$set(this.informationDialogConfig, 'message', this.$t('locations/location.confirm_deactivation'))
      }

      this.$set(this.informationDialogConfig, 'confirmCallbackFunction', () => this.onLocationActivationConfirmation(event, !!deactivatedAt))
      this.$set(this.informationDialogConfig, 'showDialog', true)
    },

    async onLocationDeleteConfirmation (event) {
      try {
        const { id } = event?.data || {}
        const { fetchTableData, resetSelections } = event || {}

        if (id) {
          await api('locations').delete(`locations/${id}`)
          this.$set(this.informationDialogConfig, 'showDialog', false)

          // Refresh table data after successfully location delete
          fetchTableData?.()
          // Reset selections after successfully location delete
          resetSelections?.()
        }
      }
      catch (exception) {
        console.error(exception)
      }
    },

    async onLocationActivationConfirmation (event, isDeactivated) {
      try {
        const { id } = event?.data || {}
        const { fetchTableData } = event || {}

        if (id) {
          await api('locations').patch(`locations/${id}/${isDeactivated ? 'activate' : 'deactivate'}`)
          this.$set(this.informationDialogConfig, 'showDialog', false)

          // Refresh table data after successfully location activation
          if (typeof fetchTableData === 'function') {
            fetchTableData()
          }
        }
      }
      catch (exception) {
        console.error(exception)
      }
    },

    onCloseInformationDialog () {
      this.$set(this.informationDialogConfig, 'showDialog', false)
    },

    showLocationDetails (event) {
      const { id } = event?.data || {}

      if (!id || this.locationType !== 4) {
        return
      }

      this.editLocationData = event.data
      this.editRoute = `locations/${id}`
      this.tableVisibility = false
      this.formDataEdit = true
      this.alertText = this.baseAlertText
    },

    checkCanSave (event) {
      const { jsonData } = event || {}

      if (!jsonData || !jsonData.data) {
        store.dispatch('base/notifications/push', this.$t('locations/location.missing_shape'))
        return false
      }

      return true
    },

    backFromEdit () {
      this.selectedLocations = []
      this.editLocationData = null
      this.editMode = false
      this.dataType = null
      this.locationData = null
      this.tableVisibility = true
      this.formDataEdit = false
    }
  }
}
</script>

<style scoped lang="scss">
.row {
  margin: 0;
}

.col {
  padding: 0;
}

.map-col {
  padding: 12px 12px 12px 0 !important;
  background-color: #F3F4F6;
}

.locations-map {
  border-radius: 16px;
}

@media (max-width: 600px) {
  .table-map-row {
    flex-direction: column;
  }

  .table-col {
    flex: 0.6;
  }

  .map-col {
    flex: 0.4;
  }
}
</style>
